import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { progressService } from "../../../../../services/progressService";

// Assets
import NotebookBg from "../../../../../assets/rootcause/notebook.png";
import JudgeIcon from "../../../../../assets/rootcause/judge.png";
import PleaserIcon from "../../../../../assets/rootcause/peoplepleaser.png";
import VictimIcon from "../../../../../assets/rootcause/victim.png";
import AvoiderIcon from "../../../../../assets/rootcause/avoider.png";
import RationalIcon from "../../../../../assets/rootcause/hyperrational.png";
import ControllerIcon from "../../../../../assets/rootcause/controller.png";

import MeetingPersonalityCard from "./MeetingPersonalityCard.jsx";

const CARD_WIDTH = 490;
const CARD_HEIGHT = 646;

const RootCauseSelection = ({ userId, subsectionId, onNextStep }) => {
  const [behaviours, setBehaviours] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedCard, setSelectedCard] = useState(null);
  const [, setSaving] = useState(false);

  // ---------------------- STATIC CARD DATA -------------------------
  // const CARDS = useMemo([
  //   {
  //     id: "judge",
  //     title: "The Judge",
  //     icon: JudgeIcon,
  //     subtitle:
  //       "I'm my own harshest critic. I’m always questioning if I’m good enough.",
  //     traits: "I overthink, stay quiet in big rooms, and nitpick my own work",
  //     likes: "Logic, structure, and doing things perfectly",
  //     dislikes: "Public mistakes, vague praise, and uncertainty",
  //   },
  //   {
  //     id: "pleaser",
  //     title: "People Pleaser",
  //     icon: PleaserIcon,
  //     subtitle: "I'm the agreeable teammate who can’t say no.",
  //     traits:
  //       "I take on too much, avoid confrontation, and smile through stress",
  //     likes: "Being liked, approval, and harmony",
  //     dislikes: "Conflict, rejection, and disappointing others",
  //   },
  //   {
  //     id: "victim",
  //     title: "The Victim",
  //     icon: VictimIcon,
  //     subtitle: "I sometimes tell the story of my misfortune.",
  //     traits:
  //       "I focus on problems more than fixes and wait for someone to rescue me",
  //     likes: "Sympathy, validation, and understanding",
  //     dislikes: "Accountability, tough feedback, and responsibility",
  //   },
  //   {
  //     id: "avoider",
  //     title: "The Avoider",
  //     icon: AvoiderIcon,
  //     subtitle:
  //       "I change the topic when tension rises, agree silently, and delay decisions.",
  //     traits: "I change the topic when tension rises and delay decisions",
  //     likes: "Calm rooms, predictable routines, and positive vibes",
  //     dislikes: "Arguments, pressure, and public disagreement",
  //   },
  //   {
  //     id: "hyper_rational",
  //     title: "The Hyper-Rational",
  //     icon: RationalIcon,
  //     subtitle: "I'm a logic-driven mind that sometimes forgets the heart.",
  //     traits: "I question emotions, demand data, and struggle with grey areas",
  //     likes: "Facts, frameworks, and evidence",
  //     dislikes: "Emotional talk, guesswork, and intuition-based decisions",
  //   },
  //   {
  //     id: "controller",
  //     title: "The Controller",
  //     icon: ControllerIcon,
  //     subtitle: "I'm the bossy driver who tends to take over every room.",
  //     traits: "I interrupt, correct others, and micromanage",
  //     likes: "Order, control, and getting things done my way",
  //     dislikes: "Slow teams, differing opinions, and loss of authority",
  //   },
  // ],[]);

  const CARDS = useMemo(() => [
    {
      id: "judge",
      title: "The Judge",
      icon: JudgeIcon,
      subtitle:
        "I'm my own harshest critic. I’m always questioning if I’m good enough.",
      traits: "I overthink, stay quiet in big rooms, and nitpick my own work",
      likes: "Logic, structure, and doing things perfectly",
      dislikes: "Public mistakes, vague praise, and uncertainty",
    },
    {
      id: "pleaser",
      title: "People Pleaser",
      icon: PleaserIcon,
      subtitle: "I'm the agreeable teammate who can’t say no.",
      traits:
        "I take on too much, avoid confrontation, and smile through stress",
      likes: "Being liked, approval, and harmony",
      dislikes: "Conflict, rejection, and disappointing others",
    },
    {
      id: "victim",
      title: "The Victim",
      icon: VictimIcon,
      subtitle: "I sometimes tell the story of my misfortune.",
      traits:
        "I focus on problems more than fixes and wait for someone to rescue me",
      likes: "Sympathy, validation, and understanding",
      dislikes: "Accountability, tough feedback, and responsibility",
    },
    {
      id: "avoider",
      title: "The Avoider",
      icon: AvoiderIcon,
      subtitle:
        "I change the topic when tension rises, agree silently, and delay decisions.",
      traits: "I change the topic when tension rises and delay decisions",
      likes: "Calm rooms, predictable routines, and positive vibes",
      dislikes: "Arguments, pressure, and public disagreement",
    },
    {
      id: "hyper_rational",
      title: "The Hyper-Rational",
      icon: RationalIcon,
      subtitle: "I'm a logic-driven mind that sometimes forgets the heart.",
      traits: "I question emotions, demand data, and struggle with grey areas",
      likes: "Facts, frameworks, and evidence",
      dislikes: "Emotional talk, guesswork, and intuition-based decisions",
    },
    {
      id: "controller",
      title: "The Controller",
      icon: ControllerIcon,
      subtitle: "I'm the bossy driver who tends to take over every room.",
      traits: "I interrupt, correct others, and micromanage",
      likes: "Order, control, and getting things done my way",
      dislikes: "Slow teams, differing opinions, and loss of authority",
    },
  ], []);

  // --------- LOAD BEHAVIOURS + PREVIOUS SELECTION -------
  useEffect(() => {
    async function loadData() {
      try {
        const res = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "presence",
          "belief_widget"
        );

        setBehaviours(res?.meeting_behaviors || []);

        const progress = await progressService.getUserProgress(
          userId,
          subsectionId
        );
        const saved = progress?.data?.selectedRootCause;

        if (saved) {
          const idx = CARDS.findIndex((c) => c.id === saved.id);
          if (idx !== -1) {
            setActiveIndex(idx);
            setSelectedCard(saved);
          }
        }
      } catch (err) {
        console.error("Error loading root cause:", err);
      }
    }
    loadData();
  }, [userId, subsectionId, CARDS]);

  // -------------------- NAVIGATION ---------------------------
  const goPrev = () =>
    setActiveIndex((prev) => (prev - 1 + CARDS.length) % CARDS.length);

  const goNext = () => setActiveIndex((prev) => (prev + 1) % CARDS.length);

  // -------------------- SAVE ENTIRE CARD ---------------------
  const handleFinish = async () => {
    const card = CARDS[activeIndex];

    try {
      setSaving(true);

      await progressService.saveProgress(
        userId,
        subsectionId,
        {
          selectedRootCause: card, // SAVE FULL OBJECT
        },
        "completed"
      );

      setSelectedCard(card);
      onNextStep?.();
    } catch (err) {
      console.error("Error saving root cause selection:", err);
    } finally {
      setSaving(false);
    }
  };

  const leftIndex = (activeIndex - 1 + CARDS.length) % CARDS.length;
  const rightIndex = (activeIndex + 1) % CARDS.length;

  return (
    <div className="w-full flex flex-col items-center">
      <div className="flex w-full max-w-6xl gap-10 justify-center mt-10">
        {/* NOTEBOOK */}
        <div className="relative" style={{ width: 420, height: 540 }}>
          <img
            src={NotebookBg}
            alt="notebook-background"
            className="absolute inset-0 w-full h-full object-contain"
          />

          <div
            className="absolute"
            style={{ top: 70, left: 115, right: 40, bottom: 110 }}
          >
            <h3 className="text-[#169E9C] text-[20px] mb-4">My Behaviours</h3>
            <div className="w-full h-[1px] bg-black mb-3"></div>

            <ul className="space-y-3 text-md font-bradleyBold leading-tight">
              {behaviours.map((b, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-[6px] h-[6px] bg-black rounded-full mt-2" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CAROUSEL */}
        <div className="relative" style={{ width: 634, height: 686, left: 15 }}>
          {/* LEFT */}
          <motion.div
            className="absolute"
            style={{
              width: 345,
              height: 425,
              top: 87,
              left: 10,
              borderRadius: 17,
              border: "0.85px solid #54B0AF",
              zIndex: 1, // <<-- Fix
            }}
          >
            <MeetingPersonalityCard
              mode="data"
              data={CARDS[leftIndex]}
              compact
            />
          </motion.div>

          {/* CENTER */}
          <motion.div
            className="absolute"
            style={{
              top: 21,
              left: 72,
              width: CARD_WIDTH,
              height: CARD_HEIGHT,
              zIndex: 5, // <<-- Always above others
            }}
          >
            <MeetingPersonalityCard
              mode="data"
              data={CARDS[activeIndex]}
              selectable
              selected={selectedCard?.id === CARDS[activeIndex].id}
              onSelect={() => setSelectedCard(CARDS[activeIndex])}
            />
          </motion.div>

          {/* RIGHT */}
          <motion.div
            className="absolute"
            style={{
              width: 345,
              height: 425,
              top: 87,
              left: 251,
              borderRadius: 17,
              border: "0.85px solid #54B0AF",
              zIndex: 1, // <<-- Fix
            }}
          >
            <MeetingPersonalityCard
              mode="data"
              data={CARDS[rightIndex]}
              compact
            />
          </motion.div>

          {/* ARROWS */}
          <button
            onClick={goPrev}
            className="absolute left-[-25px] top-1/2 -translate-y-1/2 bg-black text-white w-10 h-10 rounded-full flex items-center justify-center shadow"
          >
            <ChevronLeft size={18} />
          </button>

          <button
            onClick={goNext}
            className="absolute right-[-25px] top-1/2 -translate-y-1/2 bg-black text-white w-10 h-10 rounded-full flex items-center justify-center shadow"
          >
            <ChevronRight size={18} />
          </button>

          {/* DOTS */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[-35px] flex gap-2">
            {CARDS.map((_, i) => (
              <div
                key={i}
                className={`
        w-3 h-3 rounded-full transition-all duration-300
        ${i === activeIndex ? "bg-[#54B0AF]" : "bg-gray-300"}
      `}
              />
            ))}
          </div>
        </div>
<button
  onClick={handleFinish}
  className="
    absolute bottom-[25px] left-[120px]
    w-[260px] h-[69px]
    bg-[#54B0AF] text-white
    rounded-[20px]
    flex items-center justify-between
    px-[20px]
    text-[18px] font-medium font-gotham
    shadow-md
  "
>
  Finish Selection

  {/* Right Circle with Arrow */}
  <span
    className="
      w-[32px] h-[32px]
      bg-white/30
      rounded-full flex items-center justify-center
    "
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  </span>
</button>

      </div>
    </div>
  );
};

export default RootCauseSelection;
